import {
  BookOpenIcon,
  SmartphoneIcon,
  ZapIcon,
  GaugeIcon,
  WrenchIcon,
  LibraryIcon,
  CircleHelpIcon,
  SearchIcon,
  RefreshCwIcon,
  type LucideIcon,
} from 'lucide-react';

export const BRAND = 'EbookStore';
export const PRODUCT_NAME = 'Kit Mantenimiento Total del Auto';
export const CHECKOUT_URL = 'https://kit-mantenimiento-total-del-auto.impultienda.ar/checkout';
export const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/wUVngkJQRTef7k2r5Nt82s/Diseno_sin_titulo.png';
export const PRICE = '$19.990 ARS';
export const PRICE_AMOUNT = '$19.990';
export const PRICE_CURRENCY = 'ARS';
export const SUPPORT_EMAIL = 'ebookstore028@gmail.com';

export const heroBadges: { label: string; icon: LucideIcon }[] = [
  { label: '4 volúmenes + 5 bonos', icon: BookOpenIcon },
  { label: '100% digital', icon: SmartphoneIcon },
  { label: 'Acceso inmediato', icon: ZapIcon },
];

export type FamiliarProblem = {
  icon: LucideIcon;
  title: string;
  tags: string[];
  tagStyle: 'chips' | 'lines';
  question?: { lead: string; text: string };
  paragraphs: string[];
  flow?: string[];
  closing?: string[];
};

export const familiarProblems: FamiliarProblem[] = [
  {
    icon: GaugeIcon,
    title: 'Se prendió una luz y no sabés qué significa',
    tags: ['Check Engine', 'Aceite', 'Batería', 'Temperatura', 'Frenos', 'ABS'],
    tagStyle: 'chips',
    paragraphs: [
      'Aparece un símbolo en el tablero y empezás a buscar respuestas en Google, videos o foros sin saber si realmente aplican a tu vehículo.',
    ],
  },
  {
    icon: WrenchIcon,
    title: 'Sabés manejarlo... ¿pero sabés qué revisar?',
    tags: ['Aceite', 'Neumáticos', 'Fluidos', 'Batería', 'Frenos', 'Filtros', 'Correas', 'Refrigeración', 'Service'],
    tagStyle: 'chips',
    paragraphs: [
      'Sabés que el auto necesita mantenimiento, pero no siempre sabés qué revisar, cuándo hacerlo o qué información guardar.',
    ],
  },
  {
    icon: LibraryIcon,
    title: 'La información está en todos lados',
    tags: ['Videos', 'Foros', 'Manuales', 'Redes sociales', 'Consejos de conocidos', 'Talleres', 'Páginas web'],
    tagStyle: 'chips',
    paragraphs: [
      'La información existe.',
      'El problema es que está dispersa y muchas veces no sabés qué información corresponde a tu situación.',
    ],
    closing: ['No necesitás más información suelta.', 'Necesitás un sistema organizado.'],
  },
  {
    icon: CircleHelpIcon,
    title: 'Cambiar cosas sin entender el problema puede hacerte perder tiempo y dinero',
    tags: ['Escuchás un ruido.', 'Se prende un testigo.', 'El auto vibra.', 'Aumenta el consumo.', 'Le cuesta arrancar.'],
    tagStyle: 'lines',
    question: { lead: 'Y aparece la clásica pregunta:', text: '“¿Qué será?”' },
    paragraphs: [
      'Un síntoma puede tener distintas causas.',
      'Por eso el objetivo no es enseñarte a adivinar qué pieza está fallando.',
      'El objetivo es ayudarte a:',
    ],
    flow: ['Identificar', 'Observar', 'Registrar', 'Clasificar', 'Consultar'],
  },
];

export const carouselImages = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/vwZDpm3PEirggdthgnrmFs/Captura_de_pantalla_2026-10-02_155605.png',
    alt: 'Página “Tu auto te habla”: comparación entre confusión y control',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/qS6dy4dkTLu1ystywG2z88/Captura_de_pantalla_2026-10-02_155621.png',
    alt: 'Página “Qué hay debajo del capot” con los componentes señalados',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/pJjkxPZhJSJ5obJ96ZWQGR/Captura_de_pantalla_2026-10-02_155931.png',
    alt: 'Página “Identificar el sistema de aceite”: tapa, varilla e indicador electrónico',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/59Yx1sRTgwyycLBSVdzY5e/Captura_de_pantalla_2026-10-02_155809.png',
    alt: 'Página “Visibilidad: antes de manejar” con checklist de control',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/81AgLVGto9h3jG52RKRWPo/Captura_de_pantalla_2026-10-02_155907.png',
    alt: 'Página “Qué hay debajo del capot”: depósitos, batería y filtro de aire',
  },
];

export type ValueFeature = {
  icon: LucideIcon;
  title: string;
  body?: string;
  stages?: string[];
  listLead?: string;
  list?: string[];
};

export const valueFeatures: ValueFeature[] = [
  {
    icon: WrenchIcon,
    title: 'Información práctica',
    body: 'Encontrá rápidamente el sistema, síntoma, control o mantenimiento que necesitás consultar.',
  },
  {
    icon: SearchIcon,
    title: 'Método organizado',
    body: 'En lugar de saltar entre información dispersa, seguí una estructura clara.',
  },
  {
    icon: ZapIcon,
    title: '4 etapas conectadas',
    stages: ['Conocer', 'Prevenir', 'Detectar', 'Actuar'],
  },
  {
    icon: RefreshCwIcon,
    title: 'Usalo una y otra vez',
    body: 'No está pensado para leerlo una sola vez.',
    listLead: 'Volvé cuando:',
    list: [
      'se prende una luz,',
      'tenés que hacer un service,',
      'escuchás un ruido,',
      'vas a viajar,',
      'llevás el auto al taller,',
      'o simplemente querés comprobar qué mantenimiento realizaste.',
    ],
  },
];

export const valueClosing = ['Menos búsquedas.', 'Menos dudas.', 'Más control sobre tu auto.'];

export const offerIncludes = [
  'Tu Auto Desde Cero',
  'Mantenimiento Preventivo Total',
  'Detectá las Fallas a Tiempo',
  'Emergencias del Auto',
  'Plan Maestro de Mantenimiento',
  'Guía Visual del Tablero',
  'Kit Ruta Argentina',
  'Historial Completo de Mi Auto',
  'Guía para Ir al Taller',
];

export const testimonials = [
  {
    initials: 'MR',
    name: 'Martín Rodríguez',
    location: 'Córdoba Capital',
    quote:
      'Siempre tuve auto pero sabía muy poco de mantenimiento. Lo que más me sirvió fue tener todo ordenado y poder entender mejor qué revisar antes de llevarlo al taller.',
  },
  {
    initials: 'LF',
    name: 'Luciana Fernández',
    location: 'Rosario, Santa Fe',
    quote:
      'La guía del tablero y el plan de mantenimiento me resultaron súper prácticos. Antes tenía que buscar cada cosa por separado y ahora tengo todo en un mismo lugar.',
  },
  {
    initials: 'FG',
    name: 'Federico Gómez',
    location: 'Mar del Plata, Buenos Aires',
    quote:
      'Me gustó especialmente porque está explicado para alguien que no es mecánico. Ahora llevo mejor registro de los services y sé qué información anotar cuando noto algo raro en el auto.',
  },
];

export const faqs: { q: string; a: string[] }[] = [
  {
    q: '¿Para quién es este sistema?',
    a: [
      'Para conductores y propietarios de vehículos que quieren comprender mejor su auto, organizar su mantenimiento y disponer de herramientas prácticas para situaciones cotidianas.',
    ],
  },
  { q: '¿Es un producto físico?', a: ['No.', 'Kit Mantenimiento Total del Auto es un producto 100% digital.'] },
  { q: '¿Necesito saber mecánica?', a: ['No.', 'Está pensado especialmente para principiantes.'] },
  {
    q: '¿Es un curso de mecánica?',
    a: ['No.', 'Es un sistema visual y práctico de consulta, organización y aprendizaje para propietarios de vehículos.'],
  },
  {
    q: '¿Me enseña a reparar el auto?',
    a: [
      'No está diseñado para reemplazar la formación de un mecánico profesional.',
      'Te ayuda a comprender, revisar, organizar, detectar señales y saber cuándo corresponde pedir asistencia.',
    ],
  },
  {
    q: '¿Sirve para cualquier auto?',
    a: [
      'Los principios generales pueden aplicarse a muchos vehículos.',
      'Las especificaciones, intervalos, fluidos, presiones y procedimientos particulares deben verificarse siempre según el manual del fabricante.',
    ],
  },
  { q: '¿Qué recibo?', a: ['Recibís:', '4 volúmenes principales + 5 bonos complementarios.'] },
  {
    q: '¿Cómo lo recibo?',
    a: ['Después de completar la compra recibís acceso digital al material según el sistema de entrega configurado en EbookStore.'],
  },
  {
    q: '¿Puedo consultarlo desde el celular?',
    a: ['Sí.', 'Está pensado para poder consultarse digitalmente desde dispositivos compatibles.'],
  },
  { q: '¿Es un pago único?', a: ['Sí.', '$19.990 ARS', 'Pago único por el sistema completo.'] },
];

export const lastQuestion = {
  moments: [
    'Cuando tu auto hace un ruido extraño...',
    'cuando se prende un testigo...',
    'cuando llega el momento del service...',
    'cuando estás por salir a la ruta...',
    'o cuando el mecánico empieza a explicarte qué encontró...',
  ],
  question: '¿Sabés qué deberías observar, registrar o preguntar?',
  paragraphs: [
    'Dejá de saltar entre videos, búsquedas y consejos aislados.',
    'Tené un sistema organizado para acompañarte en cada etapa.',
  ],
  closing: ['Conocé tu auto.', 'Prevení problemas.', 'Detectá las señales.', 'Sabé cómo actuar.'],
};

export const finalStages = ['Conocer', 'Revisar', 'Mantener', 'Detectar', 'Registrar', 'Actuar'];

export const recentBuyers = [
  { name: 'Mariana López', city: 'Tafí del Valle', province: 'Tucumán' },
  { name: 'Lucas Fernández', city: 'Purmamarca', province: 'Jujuy' },
  { name: 'Sofía Martínez', city: 'El Calafate', province: 'Santa Cruz' },
  { name: 'Martín Gómez', city: 'Villa General Belgrano', province: 'Córdoba' },
  { name: 'Valeria Díaz', city: 'Cafayate', province: 'Salta' },
  { name: 'Diego Romero', city: 'San Martín de los Andes', province: 'Neuquén' },
  { name: 'Carolina Sosa', city: 'Esquel', province: 'Chubut' },
  { name: 'Federico Álvarez', city: 'Villa Gesell', province: 'Buenos Aires' },
  { name: 'Lorena Torres', city: 'Merlo', province: 'San Luis' },
  { name: 'Pablo Ruiz', city: 'Chilecito', province: 'La Rioja' },
  { name: 'Gabriela Benítez', city: 'Gualeguaychú', province: 'Entre Ríos' },
  { name: 'Nicolás Herrera', city: 'Tandil', province: 'Buenos Aires' },
  { name: 'Florencia Castro', city: 'Humahuaca', province: 'Jujuy' },
  { name: 'Javier Medina', city: 'Puerto Madryn', province: 'Chubut' },
  { name: 'Natalia Acosta', city: 'Villa La Angostura', province: 'Neuquén' },
  { name: 'Sebastián Molina', city: 'Malargüe', province: 'Mendoza' },
];